/**
 * The honeymoon page's addresses with the board on: Your trips and Explore are paths, not `?tab=`.
 *
 *   /:eventHandle/edit/honeymoon/trips            the couple's boards
 *   /:eventHandle/edit/honeymoon/trips/:boardId   one board (`?view=itinerary` still picks its face)
 *   /:eventHandle/edit/honeymoon/explore          the destination guide and search
 *   /:eventHandle/edit/honeymoon/explore/stays    the full hotel search, inside the page
 *   /:eventHandle/edit/honeymoon/explore/cars     the full car search, inside the page
 *
 * With the board off the page is the old landing at `/honeymoon`, so nothing here is linked to.
 * Links made before the paths existed (`?tab=plan&board=…`, `?tab=browse`) are still out there in
 * shared board links, so the page reads them and moves them onto these paths.
 */

/**
 * On Explore's searches: the board a search was opened for (a board's "+ Add", Explore while adding to
 * a board). The page says so in a banner and every save goes straight to that board.
 */
export const HONEYMOON_ADDING_TO_PARAM = 'addingTo';

/** The query keys the page used before the paths, read only to move old links over. */
export const LEGACY_HONEYMOON_TAB_PARAM = 'tab';
export const LEGACY_HONEYMOON_BOARD_PARAM = 'board';

export type HoneymoonRouteTab = 'plan' | 'browse';

/** A page of Explore's own, under its tab: the hotel or the car search. */
export type HoneymoonExploreSection = 'stays' | 'cars';

const EXPLORE_SECTIONS: ReadonlyArray<string> = ['stays', 'cars'] satisfies ReadonlyArray<HoneymoonExploreSection>;
const isExploreSection = (value: string): value is HoneymoonExploreSection => EXPLORE_SECTIONS.includes(value);

export type HoneymoonRoute = Readonly<{ tab: HoneymoonRouteTab; boardId?: string; section?: HoneymoonExploreSection }>;

const TRIPS_SEGMENT = 'trips';
const EXPLORE_SEGMENT = 'explore';

export const honeymoonRootPath = (eventHandle: string): string => `/${eventHandle}/edit/honeymoon`;

export const honeymoonTripsPath = (eventHandle: string, boardId?: string | null): string =>
  `${honeymoonRootPath(eventHandle)}/${TRIPS_SEGMENT}${boardId ? `/${encodeURIComponent(boardId)}` : ''}`;

export const honeymoonExplorePath = (eventHandle: string, section?: HoneymoonExploreSection): string =>
  `${honeymoonRootPath(eventHandle)}/${EXPLORE_SEGMENT}${section ? `/${section}` : ''}`;

/** A guest hotel or car search URL's query, moved onto Explore's own search for that product. */
const toExploreUrl = (eventHandle: string, section: HoneymoonExploreSection, guestUrl: string): string => {
  const query = guestUrl.split('?')[1];
  return `${honeymoonExplorePath(eventHandle, section)}${query ? `?${query}` : ''}`;
};

export const toHoneymoonExploreStaysUrl = (eventHandle: string, guestUrl: string): string => toExploreUrl(eventHandle, 'stays', guestUrl);
export const toHoneymoonExploreCarsUrl = (eventHandle: string, guestUrl: string): string => toExploreUrl(eventHandle, 'cars', guestUrl);

/** The path for a tab, and for "Your trips" the board open on it. */
export const honeymoonRoutePath = (eventHandle: string, route: HoneymoonRoute): string =>
  route.tab === 'browse' ? honeymoonExplorePath(eventHandle, route.section) : honeymoonTripsPath(eventHandle, route.boardId);

const safeDecode = (segment: string): string | null => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
};

/**
 * The tab (and board) a path under this event's honeymoon page names, or null for the bare page or
 * anything it does not know, which the page sends to a known path.
 */
export const parseHoneymoonPath = (pathname: string, eventHandle: string): HoneymoonRoute | null => {
  const root = honeymoonRootPath(eventHandle);
  if (!pathname.startsWith(root)) return null;
  const segments = pathname
    .slice(root.length)
    .split('/')
    .filter(segment => segment !== '');
  if (segments[0] === EXPLORE_SEGMENT && segments.length === 1) return { tab: 'browse' };
  if (segments[0] === EXPLORE_SEGMENT && segments.length === 2 && isExploreSection(segments[1])) return { tab: 'browse', section: segments[1] };
  if (segments[0] === TRIPS_SEGMENT && segments.length === 1) return { tab: 'plan' };
  if (segments[0] === TRIPS_SEGMENT && segments.length === 2) {
    const boardId = safeDecode(segments[1]);
    return boardId ? { tab: 'plan', boardId } : null;
  }
  return null;
};

/** Plain segments only: no `.` or `%` (so no `..`, encoded or not, climbing out of the page) and no `//`. */
const PAGE_SUBPATH = /^(?:\/[\w-]+)*\/?(?:\?.*)?$/;

/** True for the bare page or a path under it, with any query: `/honeymoon`, `/honeymoon/explore?x`. */
export const isHoneymoonPagePath = (path: string, eventHandle: string): boolean => {
  const root = honeymoonRootPath(eventHandle);
  return path.startsWith(root) && PAGE_SUBPATH.test(path.slice(root.length));
};
